import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { ListChecks } from "lucide-react";
import { toast } from "sonner";
import {
  IssueStatusNav,
  type StatusSelection,
} from "@/components/issues/issue-status-nav/issue-status-nav";
import { IssuesView } from "@/components/issues/issues-view/issues-view";
import { IssueFilterDrawer } from "@/components/issues/issue-filter-drawer/issue-filter-drawer";
import { IssueFilterSidebar } from "@/components/issues/issue-filter-sidebar/issue-filter-sidebar";
import { PageTitleRow } from "@/components/common/page-title-row/page-title-row";
import { FilterButton } from "@/components/common/filter-button/filter-button";
import {
  EMPTY_ISSUE_FILTERS,
  activeFilterCount,
  type IssueRefinementFilters,
} from "@/lib/issue-filters";
import { IssueDetail } from "@/components/issues/issue-detail/issue-detail";
import { IssuesReportDialog } from "@/components/issues/issues-report-dialog/issues-report-dialog";
import { reportNameFromFilters } from "@/components/issues/issues-report-dialog/report-name-from-filters";
import { useSelectionMode } from "@/components/records/selection-mode/selection-mode-provider";
import { useProjectIssueSummary, useProjectIssues } from "@/hooks/use-issues";
import { useElementWidth } from "@/hooks/use-element-width";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { useIssueStatusAccess } from "@/hooks/use-issue-status-access";
import { usePermissions } from "@/hooks/use-permissions";
import { useProject } from "@/hooks/use-project";
import { useProjectPeople } from "@/hooks/use-project-people";
import { canViewAllIssues } from "@/lib/issues";
import {
  resolveSidePanelLayout,
  sidePanelWidth,
} from "@/lib/side-panel-layout";
import { cn } from "@/lib/utils";
import {
  DEFAULT_ISSUE_ORDERING,
  isIssueOrdering,
  type IssueFilters,
  type IssueOrdering,
} from "@/lib/query-keys";
import type { IssueStatus, ProjectIssue } from "@/types/api";

/** The filter sidebar's fixed width (`w-60`), for fitting the side panel. */
const FILTER_SIDEBAR_WIDTH = 240;

const NO_ISSUES: ProjectIssue[] = [];

interface IssuesSearch {
  status?: IssueStatus;
  assigned_to?: string;
  created_by?: string;
  overdue?: boolean;
  search?: string;
  ordering?: IssueOrdering;
  /**
   * The issue open in the desktop side panel. In the URL so a panel is
   * shareable, survives a refresh, and Back closes it. See [[issues-page.spec]]
   * § Issue side panel.
   */
  issue?: string;
  /**
   * New Report → Issues: arrive already picking issues for a report, and have
   * Cancel return to the Reports page. Read by the selection-mode provider.
   * See [[selection-mode.spec]] US-009.
   */
  isReport?: boolean;
}

const NO_ISSUES_SELECTED: ReadonlySet<string> = new Set();

export const Route = createFileRoute("/projects/$projectId/issues/")({
  // The status selection and every filter live in the URL, so a filtered view
  // is linkable, survives a refresh, and the browser back button walks back
  // through the views the user actually looked at.
  validateSearch: (search: Record<string, unknown>): IssuesSearch => ({
    status: typeof search.status === "string"
      ? (search.status as IssueStatus)
      : undefined,
    assigned_to:
      typeof search.assigned_to === "string" ? search.assigned_to : undefined,
    created_by:
      typeof search.created_by === "string" ? search.created_by : undefined,
    overdue: search.overdue === true || search.overdue === "true" ? true : undefined,
    search: typeof search.search === "string" ? search.search : undefined,
    ordering: isIssueOrdering(search.ordering) ? search.ordering : undefined,
    issue: typeof search.issue === "string" ? search.issue : undefined,
    isReport:
      search.isReport === true || search.isReport === "true" ? true : undefined,
  }),
  component: IssuesPage,
});

export function IssuesPage() {
  const { projectId } = useParams({ from: "/projects/$projectId/issues/" });
  const navigate = useNavigate({ from: "/projects/$projectId/issues/" });
  const search = Route.useSearch();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Desktop opens an issue in a panel beside the list when there is room —
  // the same framing rules as the Photos page's record panel.
  const isDesktop = useIsDesktop();
  const rootRef = useRef<HTMLDivElement>(null);
  const issueListScrollRef = useRef<HTMLDivElement>(null);
  const availableWidth = useElementWidth(rootRef);
  const layout = resolveSidePanelLayout({
    isOpen: !!search.issue,
    isDesktop,
    availableWidth,
    sidebarWidth: FILTER_SIDEBAR_WIDTH,
  });
  const isSplit = layout === "split" || layout === "split-no-sidebar";
  // Desktop too narrow for the panel: the issue fills the page in place, still
  // on this route with `?issue=` — so widening the window turns it back into
  // the side panel, and closing returns to the same filtered list.
  const isFullView = layout === "fullscreen";
  const showSidebar = layout !== "split-no-sidebar" && !isFullView;

  // Phones keep the issue's own page. A shared `?issue=` link opened on one
  // goes there.
  useEffect(() => {
    if (!search.issue || layout !== "dialog") return;
    void navigate({
      to: "/projects/$projectId/issues/$issueId",
      params: { projectId, issueId: search.issue },
      replace: true,
    });
  }, [search.issue, layout, navigate, projectId]);

  const openIssue = useCallback(
    (issue: ProjectIssue) => {
      void navigate({
        search: (prev) => ({ ...prev, issue: issue.id }),
        // Opening pushes, so Back closes the panel; switching between issues
        // replaces, so Back doesn't walk through every issue clicked.
        replace: !!search.issue,
      });
    },
    [navigate, search.issue],
  );

  const closeIssue = useCallback(() => {
    void navigate({
      search: (prev) => ({ ...prev, issue: undefined }),
      replace: true,
    });
  }, [navigate]);

  // Someone who sees only the issues they're on — a contributor — gets no
  // people filters: they list the project's members. One arriving in a shared
  // address is ignored, so it can't narrow a list they have no way to clear.
  // See [[issues-page.spec]] § Only the issues you're on.
  const { data: project } = useProject(projectId);
  const { can } = usePermissions(
    project
      ? { owner: project.owner.id, my_roles: project.my_roles }
      : { owner: "", my_roles: [] },
  );
  const seesAllIssues = canViewAllIssues(can);

  const refinements: IssueRefinementFilters = useMemo(
    () => ({
      assignedTo: seesAllIssues ? (search.assigned_to ?? null) : null,
      createdBy: seesAllIssues ? (search.created_by ?? null) : null,
      overdue: search.overdue ?? false,
      search: search.search ?? "",
    }),
    [
      seesAllIssues,
      search.assigned_to,
      search.created_by,
      search.overdue,
      search.search,
    ],
  );

  const filters: IssueFilters = useMemo(
    () => ({
      status: search.status ? [search.status] : undefined,
      assigned_to: refinements.assignedTo ? [refinements.assignedTo] : undefined,
      created_by: refinements.createdBy ? [refinements.createdBy] : undefined,
      overdue: refinements.overdue ? true : undefined,
      search: refinements.search || undefined,
      ordering: search.ordering ?? DEFAULT_ISSUE_ORDERING,
    }),
    [search.status, search.ordering, refinements],
  );

  const issuesQuery = useProjectIssues(projectId, filters);
  const summaryQuery = useProjectIssueSummary(projectId, filters);
  const issues = issuesQuery.data?.issues ?? NO_ISSUES;
  // Who may post an update on which issue — the issue page's own rule, applied
  // per row so the list's swipe and status pill offer exactly what the page
  // would. See [[issues-page.spec]].
  const statusAccessFor = useIssueStatusAccess(projectId);

  const setStatus = useCallback(
    (status: StatusSelection) => {
      void navigate({
        search: (prev) => ({ ...prev, status: status ?? undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  const setOrdering = useCallback(
    (ordering: IssueOrdering) => {
      if (issueListScrollRef.current) {
        issueListScrollRef.current.scrollTop = 0;
      }
      void navigate({
        search: (prev) => ({ ...prev, ordering }),
        replace: true,
      });
    },
    [navigate],
  );

  const setRefinements = useCallback(
    (next: IssueRefinementFilters) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          assigned_to: next.assignedTo ?? undefined,
          created_by: next.createdBy ?? undefined,
          overdue: next.overdue ? true : undefined,
          search: next.search || undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const clearFilters = useCallback(() => {
    setRefinements(EMPTY_ISSUE_FILTERS);
  }, [setRefinements]);

  const filterCount = activeFilterCount(refinements);

  // ---- Selecting issues for a report ---------------------------------------
  // The provider owns the mode and the bar; this page owns the ticks, the
  // dialog and the typed name. See [[issues-page.spec]] § Selecting issues for
  // a report and [[selection-mode.spec]].
  const {
    isSelectionMode,
    startedFromReports,
    exitSelectionMode,
    setSelectedCount,
    setIsAllSelected,
    setIsSelectingAll,
    registerReportAction,
    registerSelectAllAction,
    registerUnselectAllAction,
  } = useSelectionMode();
  const [selectedIds, setSelectedIds] =
    useState<ReadonlySet<string>>(NO_ISSUES_SELECTED);
  const [reportOpen, setReportOpen] = useState(false);
  // Null until the user types: the dialog shows the filter prefill instead.
  const [typedName, setTypedName] = useState<string | null>(null);

  // A session's ticks and name die with it. Adjusted during render rather than
  // in an effect, so a new session never paints a frame of the old one's ticks.
  const [prevSelectionMode, setPrevSelectionMode] = useState(isSelectionMode);
  if (prevSelectionMode !== isSelectionMode) {
    setPrevSelectionMode(isSelectionMode);
    if (!isSelectionMode) {
      setSelectedIds(NO_ISSUES_SELECTED);
      setTypedName(null);
      setReportOpen(false);
    }
  }

  // Ticking and reading one issue are separate jobs: entering selection mode
  // closes the side panel.
  useEffect(() => {
    if (isSelectionMode && search.issue) closeIssue();
  }, [isSelectionMode, search.issue, closeIssue]);

  const toggleIssue = useCallback((issue: ProjectIssue) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(issue.id)) next.delete(issue.id);
      else next.add(issue.id);
      return next;
    });
  }, []);

  useEffect(() => {
    setSelectedCount(selectedIds.size);
  }, [selectedIds, setSelectedCount]);

  // "Unselect All" means the view is complete and every issue in it is ticked
  // — not that a count happens to match. See [[selection-mode.spec]] US-010.
  const isViewAllSelected =
    isSelectionMode &&
    !issuesQuery.isPlaceholderData &&
    !issuesQuery.hasNextPage &&
    issues.length > 0 &&
    issues.every((issue) => selectedIds.has(issue.id));
  useEffect(() => {
    setIsAllSelected(isViewAllSelected);
  }, [isViewAllSelected, setIsAllSelected]);

  // Select All loads the rest of the view before ticking it. A newer view, or
  // the end of the session, makes an in-flight run stale: its result is
  // dropped rather than ticking issues nobody is looking at.
  const selectAllRun = useRef(0);
  const viewKey = JSON.stringify(filters);
  useEffect(() => {
    selectAllRun.current += 1;
    setIsSelectingAll(false);
  }, [viewKey, isSelectionMode, setIsSelectingAll]);

  const { fetchNextPage, hasNextPage, data: issuesData } = issuesQuery;
  const handleSelectAll = useCallback(async () => {
    const run = ++selectAllRun.current;
    setIsSelectingAll(true);
    try {
      let more = hasNextPage;
      let loaded = issuesData?.issues ?? [];
      while (more) {
        // Joins a page the list's own scrolling already asked for instead of
        // cancelling it.
        const result = await fetchNextPage({ cancelRefetch: false });
        if (run !== selectAllRun.current) return;
        if (result.isFetchNextPageError || result.isError) {
          toast("Couldn't load every issue. Nothing new was selected.");
          return;
        }
        more = result.hasNextPage;
        loaded = result.data?.issues ?? loaded;
      }
      const viewIds = loaded.map((issue) => issue.id);
      setSelectedIds((prev) => new Set([...prev, ...viewIds]));
    } finally {
      if (run === selectAllRun.current) setIsSelectingAll(false);
    }
  }, [fetchNextPage, hasNextPage, issuesData, setIsSelectingAll]);

  // Unselect All mirrors it: the view's issues leave, other views' ticks stay.
  const handleUnselectAll = useCallback(() => {
    const viewIds = new Set(issues.map((issue) => issue.id));
    setSelectedIds((prev) => new Set([...prev].filter((id) => !viewIds.has(id))));
  }, [issues]);

  useEffect(() => {
    registerReportAction(() => setReportOpen(true));
  }, [registerReportAction]);
  useEffect(() => {
    registerSelectAllAction(() => void handleSelectAll());
  }, [handleSelectAll, registerSelectAllAction]);
  useEffect(() => {
    registerUnselectAllAction(handleUnselectAll);
  }, [handleUnselectAll, registerUnselectAllAction]);

  const { people } = useProjectPeople(projectId);
  const prefilledName = reportNameFromFilters(
    {
      status: search.status ?? null,
      overdue: refinements.overdue,
      assignedTo: refinements.assignedTo,
      createdBy: refinements.createdBy,
      search: refinements.search,
    },
    people,
  );

  // Generate lands on Reports. From New Report the picker's entry is replaced,
  // so Back doesn't reopen an empty picker; from Select here Reports is pushed,
  // so Back returns to the filtered list.
  const handleGenerated = useCallback(() => {
    exitSelectionMode();
    void navigate({
      to: "/projects/$projectId/reports",
      params: { projectId },
      replace: startedFromReports,
    });
  }, [exitSelectionMode, navigate, projectId, startedFromReports]);

  // The All count is the sum of every status, not a separate request — the
  // summary already carries all four and adding them is cheaper and always
  // agrees with the tabs beside it.
  const totalCount = summaryQuery.data
    ? Object.values(summaryQuery.data).reduce((sum, n) => sum + n, 0)
    : undefined;

  return (
    <div ref={rootRef} className="flex h-full overflow-hidden">
      {/*
        Desktop puts the filters beside the list, the way Photos does; mobile
        keeps them behind the button below, which the same breakpoint hides.
        Both write to the one URL-backed filter state, so neither surface can
        disagree with the other. See [[issues-page.spec]].
      */}
      {showSidebar && (
        <IssueFilterSidebar
          projectId={projectId}
          value={refinements}
          onChange={setRefinements}
          showPeople={seesAllIssues}
        />
      )}

      {/* With an issue open the panel has a fixed width per breakpoint and the
          list takes the rest. */}
      {/* Kept mounted (just hidden) behind a full-view issue, so the list
          doesn't reload when the issue closes or the window widens. */}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col overflow-hidden",
          isFullView && "hidden",
        )}
      >
        <PageTitleRow title="Issues">
          <FilterButton
            count={filterCount}
            onClick={() => setDrawerOpen(true)}
            label="Filter issues"
            // Desktop has the sidebar — unless the side panel pushed it
            // aside, when the filters move back behind this button.
            className={cn(showSidebar && "md:hidden")}
          />
        </PageTitleRow>

        {isSelectionMode && startedFromReports && <NewReportStrip />}

        <IssueStatusNav
          value={search.status ?? null}
          onChange={setStatus}
          summary={summaryQuery.data}
          totalCount={totalCount}
        />

        <div
          ref={issueListScrollRef}
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <IssuesView
            projectId={projectId}
            issues={issues}
            isLoading={issuesQuery.isLoading}
            error={issuesQuery.error}
            hasNextPage={issuesQuery.hasNextPage}
            isFetchingNextPage={issuesQuery.isFetchingNextPage}
            fetchNextPage={() => void issuesQuery.fetchNextPage()}
            onRetry={() => void issuesQuery.refetch()}
            filtered={filterCount > 0}
            onClearFilters={clearFilters}
            ordering={search.ordering ?? DEFAULT_ISSUE_ORDERING}
            onOrderingChange={setOrdering}
            // Desktop opens in place (panel, or full view when narrow);
            // phones follow the link to the issue's own page.
            onOpenIssue={isDesktop ? openIssue : undefined}
            activeIssueId={isSplit ? (search.issue ?? null) : null}
            forceCards={isSplit}
            statusAccessFor={statusAccessFor}
            selection={
              isSelectionMode
                ? { selectedIds, onToggle: toggleIssue }
                : undefined
            }
            onlyYours={!seesAllIssues}
          />
        </div>
      </div>

      {(isSplit || isFullView) && search.issue && (
        <div
          data-slot={isSplit ? "issue-side-panel" : "issue-full-view"}
          style={isSplit ? { width: sidePanelWidth(availableWidth) } : undefined}
          className={cn(
            "flex h-full flex-col overflow-hidden",
            isSplit ? "shrink-0 border-l border-border" : "min-w-0 flex-1",
          )}
        >
          {/* Keyed so a sheet or confirm left open never carries to the next
              issue. */}
          <IssueDetail
            key={search.issue}
            projectId={projectId}
            issueId={search.issue}
            onClose={closeIssue}
          />
        </div>
      )}

      <IssueFilterDrawer
        projectId={projectId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        value={refinements}
        onApply={setRefinements}
        showPeople={seesAllIssues}
      />

      <IssuesReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        projectId={projectId}
        issueIds={[...selectedIds]}
        name={typedName ?? prefilledName}
        isPrefilledName={typedName === null}
        onNameChange={setTypedName}
        onGenerated={handleGenerated}
      />
    </div>
  );
}

/**
 * Says why the list is in selection mode when the session came from New Report
 * on the Reports page. See [[issues-page.spec]].
 */
function NewReportStrip() {
  return (
    <div className="shrink-0 px-4 pb-2.5">
      <div className="flex items-center gap-2.5 rounded-md border border-border bg-card px-3 py-2.5">
        <ListChecks
          className="h-4 w-4 shrink-0 text-tertiary-ink"
          aria-hidden="true"
        />
        <p className="min-w-0 flex-1 truncate text-sm">
          <span className="font-medium">New report</span>
          <span className="text-muted-foreground">
            {" "}
            · Tick the issues to include
          </span>
        </p>
      </div>
    </div>
  );
}
